import { useNavigate } from 'react-router-dom';

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import './AdminInvoices.css';
import {
  getAdminClientInvoices,
  requestClientInvoicePayment,
  saveClientInvoice as saveClientInvoiceRequest,
} from '../../api/backend';

import {
  HiPlus,
  HiTrash,
  HiEnvelope,
  HiPencilSquare,
  HiDocumentText,
  HiCheck,
  HiPencil,
} from 'react-icons/hi2';

const BUSINESS_NAME = 'Carlo Rivera DBA BBTS';
const BUSINESS_EMAIL = 'info@bbtspr.com';
const BUSINESS_LOGO_URL = 'https://bbtspr.com/favicon.ico';
const BRAND_ORANGE = '#f97316';
const BRAND_ORANGE_DARK = '#c2410c';
const BRAND_BLUE = '#2563eb';
const BRAND_BLUE_LIGHT = '#eff6ff';
const BRAND_GRAY_BG = '#f9fafb';
const BRAND_BORDER = '#e5e7eb';
const BRAND_NOTE_BG = '#fffbeb';
const BRAND_NOTE_BORDER = '#fcd34d';
const INVOICE_FILENAME_PREFIX = 'Factura-Carlo-Rivera-BBTS';
const PARTIAL_FILENAME_PREFIX = 'Pago-Parcial-Carlo-Rivera-BBTS';

const toDateObject = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatDisplayDate = (value, locale = 'es-PR') => {
  const date = toDateObject(value);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const normalizePaymentHistory = (payments = []) =>
  (Array.isArray(payments) ? payments : [])
    .map((payment) => ({
      amount: Number(payment?.amount || 0),
      date: payment?.date || null,
      requestId: payment?.requestId || null,
    }))
    .filter((payment) => payment.amount > 0)
    .sort((a, b) => {
      const aTime = toDateObject(a.date)?.getTime() || 0;
      const bTime = toDateObject(b.date)?.getTime() || 0;
      return aTime - bTime;
    });

const buildInvoiceFinancialSnapshot = ({ invoiceData = {}, paymentRequest = null } = {}) => {
  const requestAmount = Number(paymentRequest?.amount || 0);
  const storedSnapshot = paymentRequest?.financialSnapshot || null;

  if (storedSnapshot) {
    const priorPayments = normalizePaymentHistory(storedSnapshot.priorPayments || []);
    const paymentsTotal = Number(
      storedSnapshot.paymentsTotal != null
        ? storedSnapshot.paymentsTotal
        : priorPayments.reduce((sum, payment) => sum + payment.amount, 0)
    );
    const invoiceTotal = Number(
      storedSnapshot.invoiceTotal != null ? storedSnapshot.invoiceTotal : invoiceData.total || 0
    );
    const discount = Number(
      storedSnapshot.discount != null ? storedSnapshot.discount : invoiceData.discount || 0
    );
    const balanceBeforeRequest = Number(
      storedSnapshot.balanceBeforeRequest != null
        ? storedSnapshot.balanceBeforeRequest
        : invoiceTotal - discount - paymentsTotal
    );
    const projectedBalanceAfterRequest = Number(
      storedSnapshot.projectedBalanceAfterRequest != null
        ? storedSnapshot.projectedBalanceAfterRequest
        : Math.max(balanceBeforeRequest - requestAmount, 0)
    );

    return {
      invoiceTotal,
      discount,
      requestAmount,
      priorPayments,
      paymentsTotal,
      balanceBeforeRequest,
      projectedBalanceAfterRequest,
    };
  }

  const invoiceTotal = Number(invoiceData.total || 0);
  const discount = Number(invoiceData.discount || 0);
  const priorPayments = normalizePaymentHistory(invoiceData.payments || []);
  const paymentsTotal = priorPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const balanceBeforeRequest = Math.max(invoiceTotal - discount - paymentsTotal, 0);

  return {
    invoiceTotal,
    discount,
    requestAmount,
    priorPayments,
    paymentsTotal,
    balanceBeforeRequest,
    projectedBalanceAfterRequest: Math.max(balanceBeforeRequest - requestAmount, 0),
  };
};

const buildInvoiceHtml = ({ invoiceData, clientName }) => {
  const issueDate = (toDateObject(invoiceData.createdAt) || new Date()).toLocaleDateString('es-PR', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const itemsHtml = (invoiceData.items || []).map(item => `
    <tr>
      <td style="padding: 10px 12px; border: 1px solid ${BRAND_BORDER};">${item.name || '-'}</td>
      <td style="padding: 10px 12px; border: 1px solid ${BRAND_BORDER}; color: #4b5563;">${item.description || '-'}</td>
      <td style="padding: 10px 12px; border: 1px solid ${BRAND_BORDER}; text-align: center;">${item.quantity || 1}</td>
      <td style="padding: 10px 12px; border: 1px solid ${BRAND_BORDER}; text-align: right;">$${parseFloat(item.price || 0).toFixed(2)}</td>
      <td style="padding: 10px 12px; border: 1px solid ${BRAND_BORDER}; text-align: right; font-weight: 600;">$${(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)}</td>
    </tr>
  `).join('');

  const total = parseFloat(invoiceData.total || 0);
  const discount = parseFloat(invoiceData.discount || 0);
  const paymentHistory = normalizePaymentHistory(invoiceData.payments || []);
  const abonos = paymentHistory.reduce((sum, payment) => sum + payment.amount, 0);
  const remaining = total - discount - abonos;
  const discountNote = invoiceData.discountNote?.trim();
  const depositValue = parseFloat(invoiceData.deposit || 0);
  const statusLabel = String(invoiceData.status || 'pendiente').toUpperCase();
  const requestLabels = new Map(
    (Array.isArray(invoiceData.paymentRequests) ? invoiceData.paymentRequests : []).map((request) => [
      request.id,
      request.label,
    ])
  );
  const paymentHistoryHtml = paymentHistory.length
    ? paymentHistory.map((payment) => {
        const linkedLabel = payment.requestId ? requestLabels.get(payment.requestId) : '';
        return `
          <tr>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER};">${formatDisplayDate(payment.date)}</td>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER};">${linkedLabel || 'Abono registrado'}</td>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right; font-weight:600;">${formatCurrency(payment.amount)}</td>
          </tr>
        `;
      }).join('')
    : '';

  return `
    <div style="font-family:'Segoe UI',Arial,sans-serif; max-width:800px; margin:auto; color:#1f2937; padding:24px;">
      <div style="display:flex; align-items:center; justify-content:space-between; border-bottom:3px solid ${BRAND_ORANGE}; padding-bottom:16px; margin-bottom:24px;">
        <div>
          <p style="margin:0; font-size:11px; letter-spacing:0.15em; text-transform:uppercase; color:#6b7280;">Factura Comercial</p>
          <h1 style="margin:4px 0 0; font-size:26px; font-weight:800; color:#111827;">${BUSINESS_NAME}</h1>
        </div>
        <div style="text-align:right;">
          <p style="margin:0; font-size:12px; color:#6b7280;">${issueDate}</p>
          <p style="margin:4px 0 0; font-size:12px; font-weight:600; color:${BRAND_ORANGE};">#${invoiceData.id || 'N/A'}</p>
        </div>
      </div>

      <div style="display:flex; gap:16px; margin-bottom:28px;">
        <div style="flex:1; background:${BRAND_GRAY_BG}; border-radius:10px; padding:14px 18px;">
          <p style="margin:0 0 6px; font-size:11px; text-transform:uppercase; letter-spacing:0.12em; color:#6b7280;">Cliente</p>
          <p style="margin:0; font-size:17px; font-weight:700;">${clientName || 'N/A'}</p>
          <p style="margin:4px 0 0; font-size:13px; color:#4b5563;">${invoiceData.clientEmail || 'Sin email registrado'}</p>
        </div>
        <div style="flex:1; background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:14px 18px; text-align:right;">
          <p style="margin:0 0 4px; font-size:11px; text-transform:uppercase; letter-spacing:0.12em; color:#9a3412;">Total factura</p>
          <p style="margin:0; font-size:28px; font-weight:800; color:${BRAND_ORANGE_DARK};">$${total.toFixed(2)}</p>
          <p style="margin:4px 0 0; font-size:12px; color:#7c3aed;">Depósito: $${depositValue.toFixed(2)}</p>
          <p style="margin:2px 0 0; font-size:12px; color:#6b7280;">Saldo: $${remaining.toFixed(2)}</p>
        </div>
      </div>

      <h3 style="font-size:14px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:#374151; margin-bottom:10px;">Servicios facturados</h3>
      <table style="width:100%; border-collapse:collapse; font-size:13px; margin-bottom:8px;">
        <thead>
          <tr>
            <th style="padding:10px 12px; text-align:left; border:1px solid ${BRAND_ORANGE}; background:${BRAND_ORANGE}; color:#fff;">Servicio</th>
            <th style="padding:10px 12px; text-align:left; border:1px solid ${BRAND_ORANGE}; background:${BRAND_ORANGE}; color:#fff;">Descripción</th>
            <th style="padding:10px 12px; text-align:center; border:1px solid ${BRAND_ORANGE}; background:${BRAND_ORANGE}; color:#fff;">Cant.</th>
            <th style="padding:10px 12px; text-align:right; border:1px solid ${BRAND_ORANGE}; background:${BRAND_ORANGE}; color:#fff;">Precio</th>
            <th style="padding:10px 12px; text-align:right; border:1px solid ${BRAND_ORANGE}; background:${BRAND_ORANGE}; color:#fff;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
        </tbody>
        <tfoot>
          <tr style="background:${BRAND_GRAY_BG};">
            <td colspan="4" style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right; font-weight:700;">Total</td>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right; font-weight:700;">$${total.toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="4" style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right;">Depósito solicitado</td>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right;">$${depositValue.toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="4" style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right;">Descuento</td>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right;">$${discount.toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="4" style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right;">Abonos</td>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right;">$${abonos.toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="4" style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right; font-weight:700;">Saldo restante</td>
            <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right; font-weight:700;">$${remaining.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>

      <div style="margin-top:20px;">
        <h3 style="font-size:14px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:#374151; margin-bottom:10px;">Historial de pagos aplicados</h3>
        ${
          paymentHistoryHtml
            ? `
              <table style="width:100%; border-collapse:collapse; font-size:13px;">
                <thead>
                  <tr>
                    <th style="padding:10px 12px; text-align:left; border:1px solid ${BRAND_BLUE}; background:${BRAND_BLUE}; color:#fff;">Fecha</th>
                    <th style="padding:10px 12px; text-align:left; border:1px solid ${BRAND_BLUE}; background:${BRAND_BLUE}; color:#fff;">Referencia</th>
                    <th style="padding:10px 12px; text-align:right; border:1px solid ${BRAND_BLUE}; background:${BRAND_BLUE}; color:#fff;">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  ${paymentHistoryHtml}
                </tbody>
              </table>
            `
            : `<div style="background:${BRAND_BLUE_LIGHT}; border:1px solid #bfdbfe; border-radius:8px; padding:14px 18px; font-size:13px; color:#1f2937;">Aún no hay pagos aplicados a esta factura.</div>`
        }
      </div>

      ${discountNote ? `<div style="margin-top:20px; background:${BRAND_NOTE_BG}; border:1px solid ${BRAND_NOTE_BORDER}; border-radius:8px; padding:14px 18px;">
        <p style="margin:0 0 6px; font-size:12px; font-weight:700; text-transform:uppercase; color:#92400e;">Nota del descuento</p>
        <p style="margin:0; font-size:13px; color:#1f2937; white-space:pre-line;">${discountNote}</p>
      </div>` : ''}

      <div style="margin-top:20px; background:${BRAND_NOTE_BG}; border:1px solid ${BRAND_NOTE_BORDER}; border-radius:8px; padding:14px 18px;">
        <p style="margin:0 0 6px; font-size:12px; font-weight:700; text-transform:uppercase; color:#92400e;">Notas de la factura</p>
        <p style="margin:0; font-size:13px; color:#1f2937; white-space:pre-line;">${invoiceData.notes || 'Sin notas registradas para esta factura.'}</p>
      </div>

      <div style="margin-top:20px; background:${BRAND_BLUE_LIGHT}; border:1px solid #bfdbfe; border-radius:8px; padding:14px 18px;">
        <p style="margin:0 0 6px; font-size:12px; font-weight:700; text-transform:uppercase; color:#1d4ed8;">Estado de la factura</p>
        <p style="margin:0; font-size:13px; color:#1f2937; font-weight:700;">${statusLabel}</p>
      </div>

      <p style="margin-top:32px; font-size:11px; color:#9ca3af; text-align:center; border-top:1px solid #f3f4f6; padding-top:16px;">
        ${BUSINESS_NAME} · ${BUSINESS_EMAIL} · Esta factura fue generada el ${issueDate}
      </p>
    </div>
  `;
};

const downloadInvoicePdf = async (invoiceData) => {
  const clientName = invoiceData.clientName || 'N/A';
  const html = buildInvoiceHtml({ invoiceData, clientName });

  html2pdf().from(html).save(`${INVOICE_FILENAME_PREFIX}-${invoiceData.id || 'sin-id'}.pdf`);
};

const viewInvoicePdf = async (invoiceData) => {
  let clientName = invoiceData.clientName || 'N/A';
  const html = buildInvoiceHtml({ invoiceData, clientName });
  const pdfBlob = await html2pdf().from(html).set({ format: 'a4' }).outputPdf('blob');
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const buildPartialPaymentRequestHtml = ({ invoiceData, paymentRequest }) => {
  const clientName = invoiceData.clientName || 'Cliente';
  const invoiceId = invoiceData.id || 'N/A';
  const createdAt = toDateObject(paymentRequest?.createdAt) || new Date();
  const financialSnapshot = buildInvoiceFinancialSnapshot({ invoiceData, paymentRequest });
  const {
    invoiceTotal,
    requestAmount,
    priorPayments,
    paymentsTotal,
    balanceBeforeRequest,
    projectedBalanceAfterRequest,
  } = financialSnapshot;
  const note = String(paymentRequest?.note || '').trim();
  const statusLabel = paymentRequest?.status || 'pendiente';
  const previousPaymentsHtml = priorPayments.length
    ? priorPayments.map((payment, index) => `
        <tr>
          <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER};">${index + 1}</td>
          <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER};">${formatDisplayDate(payment.date)}</td>
          <td style="padding:10px 12px; border:1px solid ${BRAND_BORDER}; text-align:right; font-weight:600;">${formatCurrency(payment.amount)}</td>
        </tr>
      `).join('')
    : '';

  return `
    <div style="font-family:'Segoe UI',Arial,sans-serif; max-width:800px; margin:auto; color:#1f2937; padding:24px;">
      <div style="display:flex; align-items:center; justify-content:space-between; border-bottom:3px solid ${BRAND_ORANGE}; padding-bottom:16px; margin-bottom:24px;">
        <div>
          <p style="margin:0 0 8px; font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; color: #6b7280;">
          Solicitud de pago parcial
          </p>
          <h1 style="margin:0; font-size:26px; font-weight:800; color:#111827;">${BUSINESS_NAME}</h1>
        </div>
        <div style="text-align:right;">
          <p style="margin:0; font-size:12px; color:#6b7280;">${createdAt.toLocaleDateString('es-PR')}</p>
          <p style="margin:4px 0 0; font-size:12px; font-weight:600; color:${BRAND_ORANGE};">#${invoiceId}</p>
        </div>
      </div>

      <div style="display:flex; gap:16px; margin-bottom:28px;">
        <div style="flex:1; background:${BRAND_GRAY_BG}; border-radius:10px; padding:14px 18px;">
          <p style="margin: 0 0 6px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b;">Cliente</p>
          <p style="margin: 0; font-size: 17px; font-weight: 700;">${clientName}</p>
          <p style="margin: 6px 0 0; color: #475569;">${invoiceData.clientEmail || 'Sin email registrado'}</p>
        </div>
        <div style="flex:1; background:${BRAND_GRAY_BG}; border:1px solid ${BRAND_BORDER}; border-radius:10px; padding:14px 18px;">
          <p style="margin: 0 0 6px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b;">Factura relacionada</p>
          <p style="margin: 0; font-size: 18px; font-weight: 700;">#${invoiceId}</p>
          <p style="margin: 6px 0 0; color: #475569;">Emitido el ${createdAt.toLocaleDateString('es-PR')}</p>
        </div>
      </div>

      <div style="background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:14px 18px; text-align:right; margin-bottom:24px;">
        <p style="margin:0 0 10px; font-size: 13px; letter-spacing: 0.18em; text-transform: uppercase; color: #9a3412;">Monto solicitado</p>
        <p style="margin: 0; font-size: 40px; font-weight: 800;">${formatCurrency(requestAmount)}</p>
      </div>

      <table style="width: 100%; border-collapse: collapse; margin-bottom: 22px; font-size: 14px;">
        <tbody>
          <tr>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; font-weight: 600;">Total factura</td>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; text-align: right;">${formatCurrency(invoiceTotal)}</td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; font-weight: 600;">Pagos realizados antes de esta solicitud</td>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; text-align: right;">${formatCurrency(paymentsTotal)}</td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; font-weight: 600;">Balance principal antes de esta solicitud</td>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; text-align: right;">${formatCurrency(balanceBeforeRequest)}</td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; font-weight: 600;">Pago parcial solicitado</td>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; text-align: right;">${formatCurrency(requestAmount)}</td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; font-weight: 600;">Balance proyectado luego de este pago</td>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; text-align: right; font-weight: 700;">${formatCurrency(projectedBalanceAfterRequest)}</td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; font-weight: 600;">Estado de la solicitud</td>
            <td style="padding: 12px; border: 1px solid ${BRAND_BORDER}; text-align: right; text-transform: capitalize;">${statusLabel}</td>
          </tr>
        </tbody>
      </table>

      <div style="margin-bottom:22px;">
        <h3 style="font-size:14px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:#374151; margin-bottom:10px;">Pagos ya realizados en esta factura</h3>
        ${
          previousPaymentsHtml
            ? `
              <table style="width:100%; border-collapse:collapse; font-size:13px;">
                <thead>
                  <tr>
                    <th style="padding:10px 12px; text-align:left; border:1px solid ${BRAND_BLUE}; background:${BRAND_BLUE}; color:#fff;">#</th>
                    <th style="padding:10px 12px; text-align:left; border:1px solid ${BRAND_BLUE}; background:${BRAND_BLUE}; color:#fff;">Fecha</th>
                    <th style="padding:10px 12px; text-align:right; border:1px solid ${BRAND_BLUE}; background:${BRAND_BLUE}; color:#fff;">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  ${previousPaymentsHtml}
                </tbody>
              </table>
            `
            : `<div style="background:${BRAND_BLUE_LIGHT}; border:1px solid #bfdbfe; border-radius:8px; padding:14px 18px; font-size:13px; color:#1f2937;">Esta solicitud se generó antes de registrar otros pagos en la factura.</div>`
        }
      </div>

      <div style="background: ${BRAND_NOTE_BG}; border: 1px solid ${BRAND_NOTE_BORDER}; border-radius: 14px; padding: 16px;">
        <p style="margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.12em; color: #9a3412;">Referencia para WhatsApp</p>
        <p style="margin: 0; font-size: 15px; line-height: 1.6;">
          Esta solicitud corresponde a la factura <strong>#${invoiceId}</strong> por un pago parcial de
          <strong> ${formatCurrency(requestAmount)}</strong>. Antes de este pago, el balance principal era de
          <strong> ${formatCurrency(balanceBeforeRequest)}</strong> y luego de aplicarlo quedaría en
          <strong> ${formatCurrency(projectedBalanceAfterRequest)}</strong>.
        </p>
        ${note ? `<p style="margin: 12px 0 0; font-size: 14px; color: #7c2d12;"><strong>Nota:</strong> ${note}</p>` : ''}
      </div>

      <div style="margin-top:20px; background:${BRAND_BLUE_LIGHT}; border:1px solid #bfdbfe; border-radius:8px; padding:14px 18px;">
        <p style="margin:0 0 6px; font-size:12px; font-weight:700; text-transform:uppercase; color:#1d4ed8;">Portal del cliente</p>
        <p style="margin:0; font-size:13px; color:#1f2937;">Puedes revisar el detalle completo entrando a tu cuenta:</p>
        <p style="margin:8px 0 0; font-size:13px;"><a href="https://bbtspr.com/login" style="color:${BRAND_BLUE};">https://bbtspr.com/login</a></p>
      </div>

      <p style="margin-top:32px; font-size:11px; color:#9ca3af; text-align:center; border-top:1px solid #f3f4f6; padding-top:16px;">
        ${BUSINESS_NAME} · ${BUSINESS_EMAIL} · Solicitud generada el ${createdAt.toLocaleDateString('es-PR')}
      </p>
    </div>
  `;
};

const downloadPartialPaymentRequestPdf = async (invoiceData, paymentRequest) => {
  if (!invoiceData?.id || !paymentRequest?.id) {
    alert('La solicitud parcial todavía no está lista para descargar.');
    return;
  }

  const html = buildPartialPaymentRequestHtml({ invoiceData, paymentRequest });
  const safeInvoiceId = String(invoiceData.id || 'factura').replace(/[^a-zA-Z0-9-_]/g, '');
  const safeRequestId = String(paymentRequest.id || 'solicitud').replace(/[^a-zA-Z0-9-_]/g, '');

  await html2pdf()
    .set({
      margin: 10,
      filename: `${PARTIAL_FILENAME_PREFIX}-${safeInvoiceId}-${safeRequestId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    })
    .from(html)
    .save();
};


const calculateRemaining = (invoice) => {
  const total = parseFloat(invoice.total || 0);
  const discount = parseFloat(invoice.discount || 0);
  const payments = (invoice.payments || []).reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);
  return total - discount - payments;
};

const getLocalDateInputValue = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const toIsoFromDateInput = (dateStr) => {
  if (!dateStr) return new Date().toISOString();
  return new Date(`${dateStr}T12:00:00`).toISOString();
};

const getDateInputValueFromPaymentDate = (value) => {
  if (!value) return getLocalDateInputValue();
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return getLocalDateInputValue();
  return getLocalDateInputValue(date);
};

const formatPaymentDate = (value) => {
  return formatDisplayDate(value, 'es-PR');
};

const NOTE_TEMPLATES = [
  {
    id: 'inicio',
    label: 'Inicio de proyecto',
    text: 'Esta factura corresponde al inicio del proyecto y reserva de agenda.',
  },
  {
    id: 'entrega',
    label: 'Entrega y cierre',
    text: 'Al completarse el pago final se realiza la entrega y cierre del proyecto.',
  },
  {
    id: 'soporte',
    label: 'Soporte post-entrega',
    text: 'Incluye soporte técnico durante el periodo acordado luego de la entrega.',
  },
];

const normalizeText = (value = '') => String(value || '').trim().toLowerCase();

const buildServiceTemplateId = (name, description) =>
  `${normalizeText(name)}|${normalizeText(description)}`;

const buildServiceTemplatesFromInvoices = (invoiceList = []) => {
  const map = new Map();

  invoiceList.forEach((invoice) => {
    (invoice.items || []).forEach((item) => {
      const name = String(item?.name || '').trim();
      if (!name) return;

      const description = String(item?.description || '').trim();
      const id = buildServiceTemplateId(name, description);
      if (!id || map.has(id)) return;

      const parsedPrice = parseFloat(item?.price);
      map.set(id, {
        id,
        name,
        description,
        price: Number.isNaN(parsedPrice) ? '' : parsedPrice,
      });
    });
  });

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'es'));
};

const buildServiceTemplatesFromItems = (items = []) => {
  const map = new Map();

  items.forEach((item) => {
    const name = String(item?.name || '').trim();
    if (!name) return;

    const description = String(item?.description || '').trim();
    const id = buildServiceTemplateId(name, description);
    if (!id || map.has(id)) return;

    const parsedPrice = parseFloat(item?.price);
    map.set(id, {
      id,
      name,
      description,
      price: Number.isNaN(parsedPrice) ? '' : parsedPrice,
    });
  });

  return Array.from(map.values());
};

const mergeServiceTemplates = (baseTemplates = [], itemTemplates = []) => {
  const merged = new Map();
  [...baseTemplates, ...itemTemplates].forEach((template) => {
    if (!template?.id || !template?.name) return;
    merged.set(template.id, template);
  });
  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name, 'es'));
};

const InvoiceModal = ({
  invoice,
  creating,
  onClose,
  onChange,
  onSave,
  onAddPayment,
  newPayment,
  setNewPayment,
  newPaymentDate,
  setNewPaymentDate,
  onViewPdf,
  sendInvoiceEmail,
  onAddPaymentRequest,
  onApplyPaymentRequest,
  serviceTemplates = [],
  noteTemplates = [],
}) => {
  const [requestAmount, setRequestAmount] = useState('');
  const [requestLabel, setRequestLabel] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [linkedRequestId, setLinkedRequestId] = useState('');
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
  const [editPaymentAmount, setEditPaymentAmount] = useState('');
  const [editPaymentDate, setEditPaymentDate] = useState(() => getLocalDateInputValue());
  const [applyRequestId, setApplyRequestId] = useState('');
  const [applyRequestAmount, setApplyRequestAmount] = useState('');
  const [applyRequestDate, setApplyRequestDate] = useState(() => getLocalDateInputValue());
  const [selectedNoteTemplate, setSelectedNoteTemplate] = useState('');
  if (!invoice) return null;

  const updateItem = (index, field, value) => {
    const updatedItems = [...(invoice.items || [])];
    const currentItem = updatedItems[index] || {};
    updatedItems[index] = {
      ...currentItem,
      [field]: value,
      ...(field === 'name' || field === 'description' ? { serviceTemplateId: '' } : {}),
    };
    onChange({ ...invoice, items: updatedItems });
  };

  const removeItem = (index) => {
    const updatedItems = [...(invoice.items || [])];
    updatedItems.splice(index, 1);
    onChange({ ...invoice, items: updatedItems });
  };

  const addItem = () => {
    const newItems = [...(invoice.items || []), { name: '', description: '', quantity: 1, price: '' }];
    onChange({ ...invoice, items: newItems });
  };

  const availableServiceTemplates = mergeServiceTemplates(
    serviceTemplates,
    buildServiceTemplatesFromItems(invoice.items || [])
  );

  const applyServiceTemplate = (index, templateId) => {
    const updatedItems = [...(invoice.items || [])];
    const currentItem = updatedItems[index] || { quantity: 1 };

    if (!templateId) {
      updatedItems[index] = {
        ...currentItem,
        serviceTemplateId: '',
      };
      onChange({ ...invoice, items: updatedItems });
      return;
    }

    const selectedTemplate = availableServiceTemplates.find((template) => template.id === templateId);
    if (!selectedTemplate) return;

    updatedItems[index] = {
      ...currentItem,
      serviceTemplateId: selectedTemplate.id,
      name: selectedTemplate.name,
      description: selectedTemplate.description || '',
      price: selectedTemplate.price === '' ? '' : selectedTemplate.price,
    };
    onChange({ ...invoice, items: updatedItems });
  };

  const getSelectedTemplateId = (item) => {
    if (item?.serviceTemplateId) return item.serviceTemplateId;
    const id = buildServiceTemplateId(item?.name, item?.description);
    const exists = availableServiceTemplates.some((template) => template.id === id);
    return exists ? id : '';
  };

  const applyNoteTemplate = (mode = 'replace') => {
    if (!selectedNoteTemplate) return;
    const template = noteTemplates.find((t) => t.id === selectedNoteTemplate);
    if (!template) return;

    const currentNotes = String(invoice.notes || '').trim();
    const templateText = template.text.trim();
    const nextNotes =
      mode === 'append' && currentNotes
        ? `${currentNotes}\n\n${templateText}`
        : templateText;

    onChange({ ...invoice, notes: nextNotes });
  };

  const calculateItemTotal = (item) => {
    const q = parseFloat(item.quantity || 0);
    const p = parseFloat(item.price || 0);
    return (q * p).toFixed(2);
  };

  const calculateInvoiceTotal = () => {
    const items = invoice.items || [];
    const total = items.reduce((sum, item) => {
      const q = parseFloat(item.quantity || 0);
      const p = parseFloat(item.price || 0);
      return sum + (q * p);
    }, 0);
    return total.toFixed(2);
  };

  
  const paymentRequests = invoice.paymentRequests || [];
  const pendingRequests = paymentRequests.filter((req) => req.status !== 'pagado');

  const startEditPayment = (payment, index) => {
    setEditingPaymentIndex(index);
    setEditPaymentAmount(String(payment?.amount ?? ''));
    setEditPaymentDate(getDateInputValueFromPaymentDate(payment?.date));
  };

  const cancelEditPayment = () => {
    setEditingPaymentIndex(null);
    setEditPaymentAmount('');
    setEditPaymentDate(getLocalDateInputValue());
  };

  const saveEditedPayment = () => {
    if (editingPaymentIndex === null) return;
    const amount = parseFloat(editPaymentAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      alert('Ingresa un monto válido.');
      return;
    }
    const payments = [...(invoice.payments || [])];
    const original = payments[editingPaymentIndex];
    payments[editingPaymentIndex] = {
      ...original,
      amount,
      date: toIsoFromDateInput(editPaymentDate),
    };

    let updatedRequests = invoice.paymentRequests || [];
    if (original?.requestId) {
      updatedRequests = updatedRequests.map((req) =>
        req.id === original.requestId
          ? { ...req, paidAt: toIsoFromDateInput(editPaymentDate), paidAmount: amount }
          : req
      );
    }

    const updatedInvoice = {
      ...invoice,
      payments,
      paymentRequests: updatedRequests,
    };

    onChange(updatedInvoice);
    onSave(updatedInvoice);
    cancelEditPayment();
  };

  const startApplyRequest = (req) => {
    setApplyRequestId(req.id);
    setApplyRequestAmount(String(req.amount ?? ''));
    setApplyRequestDate(getLocalDateInputValue());
  };

  const cancelApplyRequest = () => {
    setApplyRequestId('');
    setApplyRequestAmount('');
    setApplyRequestDate(getLocalDateInputValue());
  };

  const confirmApplyRequest = () => {
    if (!applyRequestId) return;
    const amount = parseFloat(applyRequestAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      alert('Ingresa un monto válido.');
      return;
    }
    onApplyPaymentRequest?.(applyRequestId, amount, applyRequestDate);
    cancelApplyRequest();
  };

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4 py-6">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl overflow-y-auto max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            {creating
              ? <HiPlus className="w-5 h-5 text-orange-500" />
              : <HiPencilSquare className="w-5 h-5 text-orange-500" />}
            {creating ? 'Nueva Factura' : 'Editar Factura'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none font-light"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5 space-y-7">

          {/* ── SERVICIOS ── */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Servicios facturados
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-100 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Servicio</th>
                    <th className="px-4 py-2 text-left">Descripción</th>
                    <th className="px-4 py-2 text-center w-20">Cant.</th>
                    <th className="px-4 py-2 text-right w-28">Precio</th>
                    <th className="px-4 py-2 text-right w-24">Total</th>
                    <th className="px-4 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {(invoice.items || []).map((item, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="p-2">
                        <select
                          value={getSelectedTemplateId(item)}
                          onChange={(e) => applyServiceTemplate(index, e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-300 mb-1"
                        >
                          <option value="">Nuevo servicio</option>
                          {availableServiceTemplates.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.price !== '' ? ` – $${Number(t.price).toFixed(2)}` : ''}
                            </option>
                          ))}
                        </select>
                        <input
                          value={item.name}
                          onChange={(e) => updateItem(index, 'name', e.target.value)}
                          placeholder="Nombre del servicio"
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Descripción"
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          min="1"
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={item.price}
                          onChange={(e) => updateItem(index, 'price', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                      </td>
                      <td className="p-2 text-right font-semibold text-gray-700">
                        ${calculateItemTotal(item)}
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addItem}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-800 font-medium"
            >
              <HiPlus className="w-4 h-4" />
              Añadir ítem
            </button>
          </div>

          {/* ── TOTALES ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 bg-gray-50 rounded-xl p-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Total (automático)</label>
              <input
                type="number"
                value={calculateInvoiceTotal()}
                readOnly
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Depósito solicitado</label>
              <input
                type="number"
                value={invoice.deposit}
                onChange={(e) => onChange({ ...invoice, deposit: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Descuento</label>
              <input
                type="number"
                value={invoice.discount || ''}
                onChange={(e) => onChange({ ...invoice, discount: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="col-span-2 sm:col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Nota del descuento</label>
              <textarea
                value={invoice.discountNote || ''}
                onChange={(e) => onChange({ ...invoice, discountNote: e.target.value })}
                rows={2}
                placeholder="Motivo o aclaración del descuento"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Saldo restante</label>
              <input
                type="number"
                value={calculateRemaining(invoice)}
                readOnly
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white font-semibold text-orange-700"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Estado</label>
              <select
                value={invoice.status}
                onChange={(e) => onChange({ ...invoice, status: e.target.value })}
                className={inputCls}
              >
                <option value="pendiente">Pendiente</option>
                <option value="pagada">Pagada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>
          </div>

          {/* ── NOTAS ── */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Notas</h4>
            <div className="flex flex-wrap gap-2 mb-2">
              <select
                value={selectedNoteTemplate}
                onChange={(e) => setSelectedNoteTemplate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              >
                <option value="">Seleccionar plantilla</option>
                {noteTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => applyNoteTemplate('replace')}
                className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 text-sm"
              >
                Usar plantilla
              </button>
              <button
                type="button"
                onClick={() => applyNoteTemplate('append')}
                className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 text-sm"
              >
                Añadir al final
              </button>
            </div>
            <textarea
              value={invoice.notes}
              onChange={(e) => onChange({ ...invoice, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>

          {/* ── ABONOS REGISTRADOS ── */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Abonos registrados
            </h4>
            {(invoice.payments || []).length > 0 && (
              <ul className="space-y-2 mb-4">
                {(invoice.payments || []).map((p, i) => (
                  <li key={i} className="bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                    {editingPaymentIndex === i ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="number"
                          value={editPaymentAmount}
                          onChange={(e) => setEditPaymentAmount(e.target.value)}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                        <input
                          type="date"
                          value={editPaymentDate}
                          onChange={(e) => setEditPaymentDate(e.target.value)}
                          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                        <button
                          type="button"
                          onClick={saveEditedPayment}
                          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditPayment}
                          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-green-800">${parseFloat(p.amount).toFixed(2)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{formatPaymentDate(p.date)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => startEditPayment(p, i)}
                          className="text-xs text-blue-600 hover:underline font-medium"
                        >
                          Editar
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {pendingRequests.length > 0 && (
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Vincular a solicitud de pago</label>
                <select
                  value={linkedRequestId}
                  onChange={(e) => setLinkedRequestId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">Sin solicitud</option>
                  {pendingRequests.map((req) => (
                    <option key={req.id} value={req.id}>
                      ${req.amount}{req.label ? ` · ${req.label}` : ''}{req.note ? ` · ${req.note}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <input
                type="number"
                placeholder="Monto del abono"
                value={newPayment}
                onChange={(e) => setNewPayment(e.target.value)}
                className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
              <input
                type="date"
                value={newPaymentDate}
                onChange={(e) => setNewPaymentDate(e.target.value)}
                className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
              <button
                type="button"
                onClick={() => onAddPayment(linkedRequestId)}
                className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition"
              >
                <HiPlus className="w-4 h-4" />
                Registrar abono
              </button>
            </div>
          </div>

          {/* ── SOLICITUDES DE PAGO PARCIAL ── */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Solicitudes de pago parcial
            </h4>

            {paymentRequests.length > 0 && (
              <ul className="space-y-2 mb-4">
                {paymentRequests.map((req) => {
                  const requestFinancials = buildInvoiceFinancialSnapshot({ invoiceData: invoice, paymentRequest: req });

                  return (
                    <li
                      key={req.id}
                      className={`rounded-xl px-4 py-3 border ${
                        req.status === 'pagado'
                          ? 'bg-gray-50 border-gray-100 opacity-60'
                          : 'bg-amber-50 border-amber-200'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          {req.label && (
                            <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-0.5">
                              {req.label}
                            </p>
                          )}
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(req.amount)}
                          </p>
                          {req.note && (
                            <p className="text-xs text-gray-500 mt-0.5">{req.note}</p>
                          )}
                          <p className="text-xs text-amber-700 mt-1">
                            Balance antes: {formatCurrency(requestFinancials.balanceBeforeRequest)} · Después de este pago: {formatCurrency(requestFinancials.projectedBalanceAfterRequest)}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {req.status === 'pagado' ? '✅ Completado' : '⏳ Pendiente'} ·{' '}
                            {req.createdAt ? new Date(req.createdAt).toLocaleDateString('es-PR') : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {req.status !== 'pagado' && (
                            <button
                              type="button"
                              onClick={() => startApplyRequest(req)}
                              className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-lg font-medium transition"
                            >
                              Aplicar pago
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => downloadPartialPaymentRequestPdf(invoice, req)}
                            className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-lg font-medium transition"
                          >
                            PDF
                          </button>
                        </div>
                      </div>

                      {applyRequestId === req.id && (
                        <div className="flex flex-col sm:flex-row gap-2 mt-3 pt-3 border-t border-amber-200">
                          <input
                            type="number"
                            value={applyRequestAmount}
                            onChange={(e) => setApplyRequestAmount(e.target.value)}
                            placeholder="Monto"
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                          />
                          <input
                            type="date"
                            value={applyRequestDate}
                            onChange={(e) => setApplyRequestDate(e.target.value)}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                          />
                          <button
                            type="button"
                            onClick={confirmApplyRequest}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                          >
                            Aplicar
                          </button>
                          <button
                            type="button"
                            onClick={cancelApplyRequest}
                            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm"
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Formulario nueva solicitud */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder="Nombre (ej: Depósito inicial, Pago final…)"
                  value={requestLabel}
                  onChange={(e) => setRequestLabel(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="$ Monto"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e.target.value)}
                  className="w-36 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder="Notas para el cliente (aparecen en email y SMS)"
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
                <button
                  type="button"
                  onClick={() => {
                    onAddPaymentRequest(requestAmount, requestNote, requestLabel);
                    setRequestAmount('');
                    setRequestNote('');
                    setRequestLabel('');
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition"
                >
                  Solicitar y notificar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {!creating && invoice?.id && (
              <>
                <button
                  type="button"
                  onClick={() => onViewPdf?.(invoice)}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 transition"
                >
                  <HiDocumentText className="w-4 h-4" />
                  Ver PDF
                </button>
                <button
                  type="button"
                  onClick={() => downloadInvoicePdf(invoice)}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 transition"
                >
                  <HiDocumentText className="w-4 h-4" />
                  Descargar PDF
                </button>
                <button
                  type="button"
                  onClick={() => sendInvoiceEmail(invoice)}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-50 transition"
                >
                  <HiEnvelope className="w-4 h-4" />
                  Enviar email
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => onSave()}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition"
            >
              <HiCheck className="w-4 h-4" />
              Guardar factura
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};


const AdminInvoices = () => {
  const { clientId } = useParams();
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [newPayment, setNewPayment] = useState('');
  const [newPaymentDate, setNewPaymentDate] = useState(() => getLocalDateInputValue());
  const [showModal, setShowModal] = useState(false);
  const [clientName, setClientName] = useState('');
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const navigate = useNavigate();

  const [currentClientId, setCurrentClientId] = useState('');
  const [currentClientEmail, setCurrentClientEmail] = useState('');

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!currentClientId || !currentClientEmail) return;
  
      try {
        const response = await getAdminClientInvoices(currentClientId);
        const invoiceList = (response.invoices || []).map((invoice) => ({
          ...invoice,
          clientId: response.client?.id || currentClientId,
          clientEmail: invoice.clientEmail || response.client?.email || currentClientEmail,
          clientName: invoice.clientName || response.client?.businessName || response.client?.name || '',
        }));
  
        setInvoices(invoiceList);
        setServiceTemplates(buildServiceTemplatesFromInvoices(invoiceList));
      } catch (err) {
        console.error('❌ Error al buscar facturas:', err);
      }
    };
  
    fetchInvoices();
  }, [currentClientId, currentClientEmail, refresh]);
  
  
  
  useEffect(() => {
    const fetchClient = async () => {
      const rawClientId = decodeURIComponent(clientId);

      try {
        const response = await getAdminClientInvoices(rawClientId);
        const clientRecord = response.client;

        setCurrentClientId(clientRecord.id);
        setCurrentClientEmail(clientRecord.email || rawClientId);
        setClientName(clientRecord.businessName || clientRecord.name || rawClientId);

        if (clientRecord.id !== rawClientId) {
          navigate(`/admin/clients/${clientRecord.id}/invoices`, { replace: true });
        }
      } catch (error) {
        console.error(`❌ Cliente no encontrado por UID ni por email: ${rawClientId}`, error);
      }
    };
  
    fetchClient();
  }, [clientId, navigate]);
  
  
  
  const sendInvoiceEmail = async (invoiceData) => {
    try {
      if (!invoiceData.clientEmail || invoiceData.clientEmail.trim() === '') {
        alert('⚠️ Este cliente no tiene un email asignado. No se puede enviar la factura.');
        return;
      }
  
      const html = buildInvoiceHtml({
        invoiceData: { ...invoiceData, createdAt: invoiceData.createdAt },
        clientName: invoiceData.clientName || 'N/A',
      });
  
      const pdfBlob = await html2pdf().from(html).set({ format: 'a4' }).outputPdf('blob');
  
      const base64Pdf = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            const result = reader.result.split(',')[1];
            resolve(result);
          } catch (err) {
            reject(new Error('Error al convertir el PDF a Base64'));
          }
        };
        reader.onerror = () => reject(new Error('Error al leer el PDF como Base64'));
        reader.readAsDataURL(pdfBlob);
      });
  
      const response = await fetch('https://sendclientemail-xjpm53xzha-uc.a.run.app/sendClientEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: invoiceData.clientEmail,
          subject: `Factura ${BUSINESS_NAME}`,
          text: `Adjunto encontrarás tu factura emitida por ${BUSINESS_NAME}.`,
          html: `
            <div style="font-family:'Segoe UI',Arial,sans-serif;background:#f9fafb;padding:24px;">
              <div style="max-width:560px;margin:auto;background:#fff;border-radius:12px;padding:32px;box-shadow:0 2px 12px rgba(15,23,42,0.08);">
                <div style="text-align:center;border-bottom:3px solid ${BRAND_ORANGE};padding-bottom:16px;margin-bottom:20px;">
                  <img src="${BUSINESS_LOGO_URL}" alt="${BUSINESS_NAME}" style="width:50px;height:50px;margin-bottom:8px;" />
                  <h2 style="margin:0;font-size:22px;color:#111827;">${BUSINESS_NAME}</h2>
                  <p style="margin-top:8px;color:#6b7280;">Adjunto encontrarás el PDF de tu factura.</p>
                </div>
                <div style="background:${BRAND_BLUE_LIGHT};border:1px solid #bfdbfe;border-radius:10px;padding:18px;margin-bottom:20px;color:#111827;">
                  <p style="margin:0 0 6px;font-size:15px;">Hola <strong>${invoiceData.clientName || 'cliente'}</strong>,</p>
                  <p style="margin:0;">Te compartimos tu factura con el detalle actualizado del servicio y balance correspondiente.</p>
                </div>
                <div style="text-align:center;">
                  <a href="https://bbtspr.com/login" style="display:inline-block;padding:12px 26px;background:${BRAND_ORANGE};color:#fff;border-radius:999px;text-decoration:none;font-weight:600;">
                    Entrar a mi cuenta
                  </a>
                </div>
              </div>
            </div>
          `,
          attachments: [{
            content: base64Pdf,
            filename: `${INVOICE_FILENAME_PREFIX}-${invoiceData.id || 'sin-id'}.pdf`,
            type: 'application/pdf',
            disposition: 'attachment'
          }]
        })
      });
  
      if (!response.ok) {
        const text = await response.text();
        console.error('❌ Error al enviar el email:', response.status, text);
        alert(`Error al enviar el email: ${response.status} - ${text}`);
      } else {
        console.log('📧 Email enviado correctamente ✅');
        alert('Email enviado correctamente al cliente.');
      }
  
    } catch (err) {
      console.error('❌ Error en el proceso de envío de email:', err);
      alert(`Error crítico al enviar el email: ${err.message}`);
    }
  };
  
  
  
  const saveInvoice = async (invoiceData = selectedInvoice) => {
    if (!invoiceData) return;
  
    try {
      const savedInvoice = await saveClientInvoiceRequest({
        ...invoiceData,
        clientId: invoiceData.clientId || currentClientId,
        clientEmail: invoiceData.clientEmail?.toLowerCase?.() || currentClientEmail,
        clientName: invoiceData.clientName || clientName,
      });

      setSelectedInvoice((current) => (current ? { ...current, ...savedInvoice } : savedInvoice));
  
      setCreating(false);
      setShowModal(false);
      setRefresh(r => !r);
      setNewPayment('');
    } catch (err) {
      console.error('❌ Error al guardar la factura:', err);
    }
  };
  
  // En AdminInvoices.js
const openInvoiceModal = (invoice) => {
  if (!invoice) return;
  setSelectedInvoice({
    ...invoice,
    clientEmail: currentClientEmail,
    items: invoice.items || [],
    payments: invoice.payments || [],
    paymentRequests: invoice.paymentRequests || [],
  });
  setNewPaymentDate(getLocalDateInputValue());
  setCreating(false);
  setShowModal(true);
};

const handleNew = () => {
  setCreating(true);

  // Asegúrate de incluir clientId y clientEmail en la nueva factura
  setSelectedInvoice({
    clientId: currentClientId,
    clientEmail: currentClientEmail,
    total: '',
    deposit: '',
    discount: '',
    discountNote: '',
    status: 'pendiente',
    notes: '',
    payments: [],
    paymentRequests: [],
    items: [],
  });
  
  setNewPaymentDate(getLocalDateInputValue());

  setShowModal(true);
};

const addPayment = (linkedRequestId = '') => {
  const amount = parseFloat(newPayment);
  if (isNaN(amount) || amount <= 0 || !selectedInvoice) return;

  let updatedRequests = selectedInvoice.paymentRequests || [];
  if (linkedRequestId) {
    const target = updatedRequests.find((req) => req.id === linkedRequestId);
    const requestedAmount = parseFloat(target?.amount || 0);
    if (!target) {
      alert('Solicitud de pago no encontrada.');
      return;
    }
    if (requestedAmount && amount < requestedAmount) {
      if (!window.confirm('El pago es menor al solicitado. ¿Registrar de todos modos?')) {
        return;
      }
    }
    const paidAt = toIsoFromDateInput(newPaymentDate);
    updatedRequests = updatedRequests.map((req) =>
      req.id === linkedRequestId
        ? { ...req, status: 'pagado', paidAt, paidAmount: amount }
        : req
    );
  }

  const updatedPayments = [
    ...(selectedInvoice.payments || []),
    {
      amount,
      date: toIsoFromDateInput(newPaymentDate),
      requestId: linkedRequestId || null,
    },
  ];

  const updatedInvoice = {
    ...selectedInvoice,
    paymentRequests: updatedRequests,
    payments: updatedPayments,
  };

  setSelectedInvoice(updatedInvoice);
  setNewPayment('');

  // ✅ Guardar inmediatamente el abono, esté creando o editando
  saveInvoice(updatedInvoice);
};

const applyPaymentRequest = (requestId, amountInput, dateInput) => {
  if (!selectedInvoice) return;
  const amount = parseFloat(amountInput);
  if (Number.isNaN(amount) || amount <= 0) {
    alert('Ingresa un monto válido.');
    return;
  }

  const requests = selectedInvoice.paymentRequests || [];
  const target = requests.find((req) => req.id === requestId);
  if (!target) {
    alert('Solicitud de pago no encontrada.');
    return;
  }
  if (target.status === 'pagado') {
    alert('Esta solicitud ya está marcada como pagada.');
    return;
  }

  const requestedAmount = parseFloat(target.amount || 0);
  if (requestedAmount && amount < requestedAmount) {
    if (!window.confirm('El pago es menor al solicitado. ¿Registrar de todos modos?')) {
      return;
    }
  }

  const paidAt = toIsoFromDateInput(dateInput);
  const updatedRequests = requests.map((req) =>
    req.id === requestId
      ? { ...req, status: 'pagado', paidAt, paidAmount: amount }
      : req
  );

  const updatedPayments = [
    ...(selectedInvoice.payments || []),
    { amount, date: paidAt, requestId },
  ];

  const updatedInvoice = {
    ...selectedInvoice,
    paymentRequests: updatedRequests,
    payments: updatedPayments,
  };

  setSelectedInvoice(updatedInvoice);
  saveInvoice(updatedInvoice);
};

const addPaymentRequest = async (amount, note, label) => {
  if (!selectedInvoice) return;
  const parsedAmount = parseFloat(amount);
  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    alert('Ingresa un monto válido.');
    return;
  }
  try {
    let workingInvoice = selectedInvoice;

    if (!workingInvoice.id) {
      workingInvoice = await saveClientInvoiceRequest({
        ...workingInvoice,
        clientId: workingInvoice.clientId || currentClientId,
        clientEmail: workingInvoice.clientEmail?.toLowerCase?.() || currentClientEmail,
        clientName: workingInvoice.clientName || clientName,
      });
      setSelectedInvoice(workingInvoice);
      setCreating(false);
    }

    const savedInvoice = await requestClientInvoicePayment({
      clientId: workingInvoice.clientId || currentClientId,
      invoiceId: workingInvoice.id,
      amount: parsedAmount,
      label: label?.trim() || '',
      note: note?.trim() || '',
    });
    setSelectedInvoice((current) => (current ? { ...current, ...savedInvoice } : savedInvoice));
    setRefresh((value) => !value);
  } catch (err) {
    console.error('❌ No se pudo enviar notificación de pago parcial:', err);
    alert(err?.message || 'No se pudo registrar la solicitud de pago.');
  }
};


// Estados y lógica necesarios
const getStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "pagado":
      return "bg-green-100 text-green-800";
    case "pendiente":
      return "bg-yellow-100 text-yellow-800";
    case "parcial":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const calculateRemaining = ({ total = 0, deposit = 0, discount = 0, payments = [] }) => {
  const paymentsTotal = payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);
  return parseFloat(total) - parseFloat(discount) - paymentsTotal;
};

return (
  <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
    <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 018 0v2m4 0v-2a8 8 0 00-16 0v2m16 0H5m16 0v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-1" />
            </svg>
            Facturas del Cliente
          </h2>

          <button
            onClick={handleNew}
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg shadow hover:bg-orange-600 transition text-sm"
          >
            <HiPlus className="w-5 h-5" />
            Nueva Factura
          </button>
        </div>

        {/* Tabla Desktop */}
        <div className="overflow-x-auto hidden md:block bg-white shadow rounded-lg">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
              <tr>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Depósito solicitado</th>
                <th className="px-4 py-3">Descuento</th>
                <th className="px-4 py-3">Pagos</th>
                <th className="px-4 py-3">Saldo</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-gray-800">
              {invoices.map((inv) => {
                const totalItems = (inv.items || []).reduce((sum, item) => {
                  const qty = parseFloat(item.quantity || 0);
                  const price = parseFloat(item.price || 0);
                  return sum + qty * price;
                }, 0);

                const total = inv.total !== undefined ? parseFloat(inv.total) : totalItems;
                const deposit = parseFloat(inv.deposit || 0);
                const discount = parseFloat(inv.discount || 0);
                const payments = inv.payments || [];
                const remaining = calculateRemaining({ total, deposit, discount, payments });

                return (
                  <tr
                    key={inv.id}
                    className="hover:bg-gray-50 border-b cursor-pointer"
                    onClick={() => openInvoiceModal(inv)}
                  >
                    <td className="px-4 py-3">{toDateObject(inv.createdAt)?.toLocaleDateString() || "—"}</td>
                    <td className="px-4 py-3 font-mono">{inv.id.slice(0, 6)}…</td>
                    <td className="px-4 py-3">${total.toFixed(2)}</td>
                    <td className="px-4 py-3">${deposit.toFixed(2)}</td>
                    <td className="px-4 py-3">${discount.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      ${payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">${remaining.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(inv.status)}`}>
                        {inv.status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openInvoiceModal(inv);
                        }}
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                      >
                        <HiPencil className="w-4 h-4" />
                        Editar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile view */}
        <div className="md:hidden space-y-4">
          {invoices.map((inv) => {
            const totalItems = (inv.items || []).reduce((sum, item) => {
              const qty = parseFloat(item.quantity || 0);
              const price = parseFloat(item.price || 0);
              return sum + qty * price;
            }, 0);

            const total = inv.total !== undefined ? parseFloat(inv.total) : totalItems;
            const deposit = parseFloat(inv.deposit || 0);
            const discount = parseFloat(inv.discount || 0);
            const payments = inv.payments || [];
            const remaining = calculateRemaining({ total, deposit, discount, payments });

            return (
              <div
                key={inv.id}
                className="bg-white shadow rounded-lg p-4 cursor-pointer"
                onClick={() => openInvoiceModal(inv)}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-500">
                    {toDateObject(inv.createdAt)?.toLocaleDateString() || "—"}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${getStatusColor(inv.status)}`}>
                    {inv.status || "—"}
                  </span>
                </div>
                <div className="text-sm space-y-1">
                  <p><strong>ID:</strong> <span className="font-mono">{inv.id.slice(0, 6)}…</span></p>
                  <p><strong>Total:</strong> ${total.toFixed(2)}</p>
                  <p><strong>Depósito solicitado:</strong> ${deposit.toFixed(2)}</p>
                  <p><strong>Descuento:</strong> ${discount.toFixed(2)}</p>
                  <p><strong>Pagos:</strong> ${payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0).toFixed(2)}</p>
                  <p><strong>Saldo:</strong> ${remaining.toFixed(2)}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openInvoiceModal(inv);
                  }}
                  className="mt-3 w-full text-center text-sm text-blue-600 hover:underline flex items-center justify-center gap-1"
                >
                  <HiPencil className="w-4 h-4" />
                  Editar
                </button>
              </div>
            );
          })}
        </div>

        {/* Modal */}
        {showModal && (
          <InvoiceModal
            invoice={selectedInvoice}
            creating={creating}
            onClose={() => setShowModal(false)}
            onChange={setSelectedInvoice}
            onSave={saveInvoice}
            onAddPayment={addPayment}
            onApplyPaymentRequest={applyPaymentRequest}
            onAddPaymentRequest={addPaymentRequest}
            newPayment={newPayment}
            setNewPayment={setNewPayment}
            newPaymentDate={newPaymentDate}
            setNewPaymentDate={setNewPaymentDate}
            onViewPdf={viewInvoicePdf}
            sendInvoiceEmail={sendInvoiceEmail}
            serviceTemplates={serviceTemplates}
            noteTemplates={NOTE_TEMPLATES}
          />
        )}

        {/* Botón enviar email */}
        {selectedInvoice && !showModal && !creating && (
          <div className="mt-6">
            <button
              className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition text-sm shadow"
              onClick={() => sendInvoiceEmail(selectedInvoice)}
            >
              <HiEnvelope className="w-5 h-5 text-white" />
              Enviar Factura por Email (PDF)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvoices;
